/**
 * Registers the built-in effects against the plugin registry.
 *
 * Thin adapter — each entry wraps a factory from `audio/effects.ts`
 * into the generic `EffectPlugin` shape. Hosts that read from the
 * registry will find these same kinds under the same id strings
 * the existing `BusEffect.kind` union uses (`filter`, `delay`, ...)
 * so persisted projects map cleanly once hosts migrate.
 *
 * Call `registerBuiltinPlugins()` once during engine boot — it's
 * idempotent so repeated calls are safe. The audio engine's
 * existing hardcoded switch in `Bus.buildSlot` is still
 * authoritative until the full P8b/c migration; this registration
 * just makes the same kinds discoverable through the generic
 * picker UI that P8b will add.
 */

import {
  buildDelayFx,
  buildReverbFx,
  buildBitcrushFx,
  buildStutterFx,
  buildMasterFilterFx,
  identityCurve,
  bitcrushCurve,
} from '@/audio/effects'
import { registerEffect } from './registry'
import type { EffectPlugin, PluginHandle } from './types'

let registered = false

export function registerBuiltinPlugins(): void {
  if (registered) return
  registered = true

  const delayPlugin: EffectPlugin = {
    id: 'delay',
    label: 'Delay',
    category: 'time',
    defaultPreset: { time: 0.25, feedback: 0.55, wet: 0.45 },
    paramSchema: [
      {
        id: 'time',
        label: 'Time',
        kind: 'time',
        min: 0.005,
        max: 1,
        step: 0.005,
        default: 0.25,
      },
      {
        id: 'feedback',
        label: 'Feedback',
        kind: 'slider',
        min: 0,
        max: 0.95,
        step: 0.01,
        default: 0.55,
      },
      {
        id: 'wet',
        label: 'Wet',
        kind: 'slider',
        min: 0,
        max: 1,
        step: 0.01,
        default: 0.45,
      },
    ],
    createNode: (ctx, preset) => wrapParallel(ctx, buildDelayFx(ctx), preset),
  }

  const reverbPlugin: EffectPlugin = {
    id: 'reverb',
    label: 'Reverb',
    category: 'time',
    defaultPreset: { wet: 0.4 },
    paramSchema: [
      {
        id: 'wet',
        label: 'Wet',
        kind: 'slider',
        min: 0,
        max: 1,
        step: 0.01,
        default: 0.4,
      },
    ],
    createNode: (ctx, preset) => wrapParallel(ctx, buildReverbFx(ctx), preset),
  }

  const bitcrushPlugin: EffectPlugin = {
    id: 'bitcrush',
    label: 'Bitcrush',
    category: 'distortion',
    defaultPreset: { bits: 4, wet: 0.7 },
    paramSchema: [
      {
        id: 'bits',
        label: 'Bit depth',
        kind: 'slider',
        min: 1,
        max: 12,
        step: 1,
        default: 4,
      },
      {
        id: 'wet',
        label: 'Wet',
        kind: 'slider',
        min: 0,
        max: 1,
        step: 0.01,
        default: 0.7,
      },
    ],
    createNode: (ctx, preset) => {
      const fx = buildBitcrushFx(ctx)
      const handle = wrapParallel(ctx, fx, preset)
      // Custom param interceptor for `bits` — switches the shaper
      // curve rather than a continuous gain.
      const baseSet = handle.setParam
      handle.setParam = (name, value) => {
        if (name === 'bits') {
          const depth = Math.max(1, Math.min(12, Math.floor(value)))
          fx.shaper.curve = value > 0 ? bitcrushCurve(depth) : identityCurve()
          return
        }
        baseSet(name, value)
      }
      return handle
    },
  }

  const stutterPlugin: EffectPlugin = {
    id: 'stutter',
    label: 'Stutter',
    category: 'time',
    defaultPreset: { time: 0.08, feedback: 0.95, wet: 0.75 },
    paramSchema: [
      {
        id: 'time',
        label: 'Time',
        kind: 'time',
        min: 0.005,
        max: 0.5,
        step: 0.005,
        default: 0.08,
      },
      {
        id: 'feedback',
        label: 'Feedback',
        kind: 'slider',
        min: 0,
        max: 0.99,
        step: 0.01,
        default: 0.95,
      },
      {
        id: 'wet',
        label: 'Wet',
        kind: 'slider',
        min: 0,
        max: 1,
        step: 0.01,
        default: 0.75,
      },
    ],
    createNode: (ctx, preset) => wrapParallel(ctx, buildStutterFx(ctx), preset),
  }

  const filterPlugin: EffectPlugin = {
    id: 'filter',
    label: 'Filter',
    category: 'eq',
    defaultPreset: { cutoff: 2000 },
    paramSchema: [
      {
        id: 'cutoff',
        label: 'Cutoff',
        kind: 'freq',
        min: 20,
        max: 22000,
        step: 1,
        default: 2000,
      },
    ],
    createNode: (ctx, preset) => {
      const fx = buildMasterFilterFx(ctx)
      if (preset?.cutoff !== undefined) {
        fx.filter.frequency.value = preset.cutoff
      }
      return {
        input: fx.input,
        output: fx.output,
        setParam(name, value) {
          if (name === 'cutoff') {
            fx.filter.frequency.setTargetAtTime(
              Math.max(20, Math.min(22000, value)),
              fx.filter.context.currentTime,
              0.01,
            )
          }
        },
        dispose() {
          try {
            fx.input.disconnect()
          } catch {
            /* already disconnected */
          }
        },
      }
    },
  }

  registerEffect(filterPlugin)
  registerEffect(delayPlugin)
  registerEffect(reverbPlugin)
  registerEffect(bitcrushPlugin)
  registerEffect(stutterPlugin)
}

/** Common wrap for parallel-send effects — splits input into dry +
 *  effect, sums both at the output gain. Handles wet changes via
 *  `setParam('wet', …)` and delegates other params back to the
 *  caller's factory handle via an override hook. */
function wrapParallel(
  ctx: AudioContext,
  fx: {
    input: AudioNode
    output: AudioNode
    wet: GainNode
    delay?: DelayNode
    feedback?: GainNode
  },
  preset?: Record<string, number>,
): PluginHandle {
  const input = ctx.createGain()
  const output = ctx.createGain()
  const dry = ctx.createGain()
  dry.gain.value = 1
  input.connect(dry).connect(output)
  input.connect(fx.input)
  fx.wet.connect(output)

  // Apply preset params at construction time.
  if (preset) {
    if (preset.wet !== undefined) {
      fx.wet.gain.value = Math.max(0, Math.min(1, preset.wet))
    }
    if (preset.time !== undefined && fx.delay) {
      fx.delay.delayTime.value = Math.max(0.001, Math.min(1, preset.time))
    }
    if (preset.feedback !== undefined && fx.feedback) {
      fx.feedback.gain.value = Math.max(0, Math.min(0.99, preset.feedback))
    }
  }

  return {
    input,
    output,
    setParam(name, value) {
      const t = ctx.currentTime
      if (name === 'wet') {
        fx.wet.gain.setTargetAtTime(Math.max(0, Math.min(1, value)), t, 0.01)
        return
      }
      if (name === 'time' && fx.delay) {
        fx.delay.delayTime.setTargetAtTime(
          Math.max(0.001, Math.min(1, value)),
          t,
          0.01,
        )
        return
      }
      if (name === 'feedback' && fx.feedback) {
        fx.feedback.gain.setTargetAtTime(
          Math.max(0, Math.min(0.99, value)),
          t,
          0.01,
        )
      }
    },
    dispose() {
      try {
        input.disconnect()
      } catch {
        /* already */
      }
      try {
        output.disconnect()
      } catch {
        /* already */
      }
      try {
        fx.input.disconnect()
      } catch {
        /* already */
      }
      try {
        fx.wet.disconnect()
      } catch {
        /* already */
      }
    },
  }
}
